<template>
  <Wrapper
      code="payment"
      :icon="ICONS.payment"
      @open="onClick"
      label="Оплата"
  >
    <div class="payment-section">
      <div>{{ content }}</div>
      <div
          v-if="showGiftNote"
          class="payment-section__gift s-font-3xs"
          :class="giftNoteClass"
      >
        <q-icon :name="ICONS.gift" size="12px" class="payment-section__gift-icon"/>
        <div v-if="isGiftReady && selectedGift" class="payment-section__gift-content">
          <div>Подарок за оплату наличными</div>
          <div class="payment-section__gift-title">{{ selectedGift.title }}</div>
        </div>
        <span v-else>{{ giftNote }}</span>
      </div>
    </div>
  </Wrapper>
</template>
<script lang="ts" setup>

import PaymentDialog from "@/components/Shop/VOrder/ui/Dialog/Payment.vue";
import {VOrderSectionProps, vorderSectionPropsDefault} from "./util";
import Wrapper from "./Wrapper/Wrapper.vue";
import {storeToRefs} from "pinia";
import {useStackRouter} from "@/packages/stack-router/hooks/useStackRouter";
import {ICONS} from "@/assets/icons";
import {computed, watch} from "vue";
import {useVorderState} from "@/modules/shop/store/vorder-state";
import {useCurrency} from "@/modules/shop/store/currency";
import {
    PAYMENT_GIFT_MIN_ORDER,
    useVorderPaymentGift
} from "@/modules/shop/store/vorder-payment-gift";

const vorderState = useVorderState()
const paymentGiftStore = useVorderPaymentGift()
const {formatPrice} = useCurrency()

const {attrs, currentPaymentType} = storeToRefs(vorderState)
const {
    hasGiftOptions,
    isCashPayment,
    isEligible,
    selectedGift,
    amountRemaining,
    pricePay,
} = storeToRefs(paymentGiftStore)

const {syncPaymentGift} = paymentGiftStore

const props = withDefaults(defineProps<VOrderSectionProps>(), {
  ...vorderSectionPropsDefault,
})

const stackRouter = useStackRouter()

const onClick = () => {
  stackRouter.getStack()?.push(PaymentDialog, {
    fromSection: 'payment',
  })
}

const content = computed(() => {
  if (currentPaymentType.value) {
    let res = currentPaymentType.value.NAME
    if (attrs.value.PAYMENT_TYPE === 'cash' && attrs.value.CASH_SUM && attrs.value.CASH_SUM > 0) {
      res += ' (сдача с ' + attrs.value.CASH_SUM + ')'
    }
    return res
  } else {
    return 'выберите способ оплаты'
  }
})

const giftNote = computed(() => {
  if (!hasGiftOptions.value) return null

  const minOrder = formatPrice(PAYMENT_GIFT_MIN_ORDER)
  const remaining = formatPrice(amountRemaining.value)

  if (!isCashPayment.value) {
    return `Подарок при оплате наличными от ${minOrder}`
  }

  if (isEligible.value) {
    if (selectedGift.value) {
      return null
    }
    return 'Выберите подарок'
  }

  return `Подарок при оплате наличными от ${minOrder} — не хватает ${remaining}`
})

const isGiftReady = computed(() => {
  return isCashPayment.value && isEligible.value && !!selectedGift.value
})

const showGiftNote = computed(() => {
  return hasGiftOptions.value && (isGiftReady.value || !!giftNote.value)
})

const giftNoteClass = computed(() => {
  return isGiftReady.value
      ? 'payment-section__gift--active'
      : 'payment-section__gift--promo'
})

watch(
    () => [attrs.value.PAYMENT_TYPE, pricePay.value],
    () => syncPaymentGift(),
    {immediate: true}
)

</script>
<style scoped lang="scss">
.payment-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.payment-section__gift {
  display: inline-flex;
  align-items: flex-start;
  gap: 4px;
  line-height: 1.3;
  max-width: 100%;
}

.payment-section__gift-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.payment-section__gift-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.payment-section__gift-title {
  font-weight: 500;
  line-height: 1.25;
}

.payment-section__gift--active {
  color: #2e7d32;
}

.payment-section__gift--promo {
  color: #ef6c00;
}
</style>
